/**
 * DatevTaxonomyPreviewDialogComponent — read-only full-screen preview of
 * the AI-fixed DATEV taxonomy.
 *
 * Shows the three sections (Bilanz-Aktiva, Bilanz-Passiva, GuV) in tabs.
 * Each section is rendered as an indented flat list.
 *
 * Numbering differs per statement type:
 *
 *   Balance Sheet (Bilanz-Aktiva / Bilanz-Passiva):
 *     Level 1 → A, B, C …           (upper-alpha)
 *     Level 2 → I, II, III …        (upper-roman)
 *     Level 3 → 1, 2, 3 …           (decimal)
 *     Level 4 → a, b, c …           (lower-alpha)
 *     Level 5+ → aa, ab … ba, bb …  (parent-label + lower-alpha counter)
 *
 *   Income Statement (GuV):
 *     Level 1 → 1, 2, 3 …           (decimal)
 *     Level 2 → a, b, c …           (lower-alpha)
 *     Level 3+ → aa, ab … ba … aaa  (parent-label + lower-alpha counter)
 *
 *   OF_THAT_ITEM / SUB_FLI → "–" prefix instead of a number
 *   disableNumbering = true → no prefix
 *
 * No editing — purely informational.
 */
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import {
    AiAgentProvider,
    AiChatMessage,
    AiJobProgressEvent,
    selectChatHistory,
    selectOutput,
    selectProgressEvents,
} from '@modeso/aumico-lib-ai-agents-fe';
import { AumicoNode, AumicoNodeType } from '../../helpers/datev.excel.helper';
import { AumicoSections } from '../../helpers/datev-ai.adapter';

export interface TaxonomyPreviewDialogData {
    sections: AumicoSections;
    /** Active AI session ID — when present the chat panel is shown. */
    sessionId?: string | null;
}

export interface PreviewRow {
    id: string;
    itemText: string;
    classification: string;
    level: number;
    numberLabel: string;   // computed label (A, I, 1, a, aa, …) or "–" or ""
    isGroup: boolean;
    isDash: boolean;       // OF_THAT_ITEM / SUB_FLI → shown with leading dash
}

export interface PreviewTab {
    key: string;
    label: string;
    rows: PreviewRow[];
}

/** Statement type drives the numbering scheme. */
type SectionMode = 'BS' | 'IS';

type CounterStyle = 'upper-alpha' | 'upper-roman' | 'decimal' | 'lower-alpha' | 'lower-roman' | 'compound-lower-alpha';

@Component({
    selector: 'datev-taxonomy-preview-dialog',
    templateUrl: './datev-taxonomy-preview-dialog.component.html',
    styleUrls: ['./datev-taxonomy-preview-dialog.component.scss'],
})
export class DatevTaxonomyPreviewDialogComponent implements OnInit, OnDestroy {
    tabs: PreviewTab[] = [];
    activeTabIndex = 0;

    // ── Chat ──────────────────────────────────────────────────────────
    chatMessages: AiChatMessage[] = [];
    chatProgressEvents: AiJobProgressEvent[] = [];
    chatInput = '';
    isSending = false;

    /** Brief flash shown when the tree rebuilds after a chat response. */
    treeUpdatedFlash = false;

    /** True when a sessionId was provided — enables the chat panel. */
    get hasSession(): boolean {
        return !!this.data.sessionId;
    }

    private readonly destroy$ = new Subject<void>();

    private readonly sectionConfig: Array<{ key: keyof AumicoSections; labelKey: string }> = [
        { key: 'Bilanz-Aktiva',  labelKey: 'aumico_ai_taxonomy_section_assets' },
        { key: 'Bilanz-Passiva', labelKey: 'aumico_ai_taxonomy_section_liabilities' },
        { key: 'GuV',            labelKey: 'aumico_ai_taxonomy_section_income' },
    ];

    constructor(
        public dialogRef: MatDialogRef<DatevTaxonomyPreviewDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: TaxonomyPreviewDialogData,
        private readonly aiAgentProvider: AiAgentProvider,
        private readonly store: Store,
    ) {}

    ngOnInit(): void {
        this.buildTabs();

        if (this.hasSession) {
            // Subscribe to chat history from the store
            this.store.select(selectChatHistory).pipe(
                takeUntil(this.destroy$),
            ).subscribe(messages => {
                const wasSending = this.isSending;
                this.chatMessages = messages;
                // Turn off spinner when a new agent reply arrives
                if (wasSending && messages.length > 0 && messages[messages.length - 1].role === 'agent') {
                    this.isSending = false;
                    this.chatProgressEvents = []; // Clear progress once reply arrives
                }
            });

            // Subscribe to progress events (tool calls, section status) during chat
            this.store.select(selectProgressEvents).pipe(
                takeUntil(this.destroy$),
            ).subscribe(events => {
                this.chatProgressEvents = events;
            });

            // When the agent returns updated output (sections), rebuild the tabs live
            this.store.select(selectOutput).pipe(
                takeUntil(this.destroy$),
                filter((output): output is Record<string, unknown> => output != null),
            ).subscribe(output => {
                const sections = output['sections'] as AumicoSections | undefined;
                if (sections) {
                    this.data = { ...this.data, sections };
                    this.buildTabs();

                    // Brief visual flash so the user notices the tree refreshed
                    this.treeUpdatedFlash = true;
                    setTimeout(() => (this.treeUpdatedFlash = false), 2500);
                }
            });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    selectTab(index: number): void {
        this.activeTabIndex = index;
    }

    close(): void {
        this.dialogRef.close();
    }

    // ── Chat ──────────────────────────────────────────────────────────

    sendMessage(): void {
        const text = this.chatInput.trim();
        if (!text || !this.data.sessionId || this.isSending) return;
        this.isSending = true;
        this.chatInput = '';
        const sectionKey = this.tabs[this.activeTabIndex]?.key;
        this.aiAgentProvider.sendChat(this.data.sessionId, text, sectionKey);
    }

    onChatKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.sendMessage();
        }
    }

    /** Format a progress event into a human-readable label for the log */
    formatProgressEvent(event: AiJobProgressEvent): string {
        if (event.type === 'tool_call' && event.toolName) {
            const section = event.sectionKey ? `[${event.sectionKey}]` : '';
            const errors = event.remainingErrors != null && event.remainingErrors >= 0
                ? ` → ${event.remainingErrors} error(s) left`
                : '';
            return `${section} ${event.toolName}${errors}`;
        }
        if (event['message']) {
            return String(event['message']);
        }
        return event.type?.replace(/_/g, ' ') ?? 'Processing…';
    }

    getIndentPx(level: number): string {
        return `${(level - 1) * 20}px`;
    }

    getBadgeClass(classification: string): string {
        const map: Record<string, string> = {
            [AumicoNodeType.GROUP]: 'badge--group',
            [AumicoNodeType.HIDDEN_GROUP]: 'badge--hidden-group',
            [AumicoNodeType.FLI]: 'badge--fli',
            [AumicoNodeType.FLI_WITHOUT_NUMBERING]: 'badge--fli',
            [AumicoNodeType.X_FLI]: 'badge--x-fli',
            [AumicoNodeType.SUB_FLI]: 'badge--sub-fli',
            [AumicoNodeType.SUM]: 'badge--sum',
            [AumicoNodeType.TITLE]: 'badge--title',
            [AumicoNodeType.OF_THAT_ITEM]: 'badge--of-that',
            [AumicoNodeType.REMOVE]: 'badge--remove',
        };
        return map[classification] ?? 'badge--unknown';
    }

    /** Returns the i18next key for a node's classification label */
    getClassificationKey(classification: string): string {
        const map: Record<string, string> = {
            [AumicoNodeType.GROUP]: 'aumico_ai_classification_group',
            [AumicoNodeType.HIDDEN_GROUP]: 'aumico_ai_classification_hidden_group',
            [AumicoNodeType.FLI]: 'aumico_ai_classification_fli',
            [AumicoNodeType.FLI_WITHOUT_NUMBERING]: 'aumico_ai_classification_fli',
            [AumicoNodeType.X_FLI]: 'aumico_ai_classification_x_fli',
            [AumicoNodeType.SUB_FLI]: 'aumico_ai_classification_sub_fli',
            [AumicoNodeType.SUM]: 'aumico_ai_classification_sum',
            [AumicoNodeType.TITLE]: 'aumico_ai_classification_title',
            [AumicoNodeType.OF_THAT_ITEM]: 'aumico_ai_classification_of_that_item',
            [AumicoNodeType.REMOVE]: 'aumico_ai_classification_remove',
        };
        return map[classification] ?? 'aumico_ai_classification_unknown';
    }

    // ── Private helpers ───────────────────────────────────────────────

    private buildTabs(): void {
        if (!this.data?.sections) return;

        this.tabs = this.sectionConfig
            .filter((cfg) => {
                const nodes = this.data.sections[cfg.key];
                return nodes && nodes.length > 0;
            })
            .map((cfg) => {
                const mode: SectionMode = cfg.key === 'GuV' ? 'IS' : 'BS';
                return {
                    key: cfg.key,
                    label: cfg.labelKey,
                    rows: this.flattenNodes(
                        this.data.sections[cfg.key],
                        new Map<number, number>(),
                        new Map<number, string>(),
                        mode,
                    ),
                };
            });
    }

    /**
     * Recursively flatten nodes, computing the numbering label for each node.
     *
     * `counters`  — Map<level, currentCount>   (resets deeper keys on level-up)
     * `labels`    — Map<level, currentLabel>    (used to build compound labels at deep levels)
     * `mode`      — 'BS' or 'IS' drives the level→style lookup
     */
    private flattenNodes(
        nodes: AumicoNode[],
        counters: Map<number, number>,
        labels: Map<number, string>,
        mode: SectionMode,
    ): PreviewRow[] {
        const rows: PreviewRow[] = [];
        for (const node of nodes) {
            const level = node.level ?? 1;
            const isDash =
                node.classification === AumicoNodeType.OF_THAT_ITEM ||
                node.classification === AumicoNodeType.SUB_FLI;

            // Reset all counters/labels for levels deeper than this one (moved back up the tree)
            for (const key of [...counters.keys()]) {
                if (key > level) {
                    counters.delete(key);
                    labels.delete(key);
                }
            }

            let numberLabel = '';
            if (isDash) {
                numberLabel = '–';
            } else if (!node.disableNumbering) {
                const style = mode === 'BS'
                    ? this.getBSLevelStyle(level)
                    : this.getISLevelStyle(level);

                const count = (counters.get(level) ?? 0) + 1;
                counters.set(level, count);

                if (style === 'compound-lower-alpha') {
                    const parentLabel = labels.get(level - 1) ?? '';
                    numberLabel = parentLabel + this.toAlpha(count).toLowerCase();
                } else {
                    numberLabel = this.formatByStyle(count, style);
                }
                labels.set(level, numberLabel);
            }

            rows.push({
                id: node.id ?? '',
                itemText: node.itemText ?? '',
                classification: node.classification ?? AumicoNodeType.UNKNOWN,
                level,
                numberLabel,
                isGroup:
                    node.classification === AumicoNodeType.GROUP ||
                    node.classification === AumicoNodeType.HIDDEN_GROUP,
                isDash,
            });

            if (node.children?.length) {
                rows.push(...this.flattenNodes(node.children, counters, labels, mode));
            }
        }
        return rows;
    }

    /**
     * BS numbering scheme:
     *   L1 → A B C …           (upper-alpha)
     *   L2 → I II III …        (upper-roman)
     *   L3 → 1 2 3 …           (decimal)
     *   L4 → a b c …           (lower-alpha)
     *   L5+ → aa ab … ba bb …  (compound: parent-label + lower-alpha)
     */
    private getBSLevelStyle(level: number): CounterStyle {
        switch (level) {
            case 1:  return 'upper-alpha';
            case 2:  return 'upper-roman';
            case 3:  return 'decimal';
            case 4:  return 'lower-alpha';
            default: return 'compound-lower-alpha';
        }
    }

    /**
     * IS numbering scheme:
     *   L1 → 1 2 3 …           (decimal)
     *   L2 → a b c …           (lower-alpha)
     *   L3+ → aa ab … aaa …    (compound: parent-label + lower-alpha)
     */
    private getISLevelStyle(level: number): CounterStyle {
        switch (level) {
            case 1:  return 'decimal';
            case 2:  return 'lower-alpha';
            default: return 'compound-lower-alpha';
        }
    }

    /** Convert count + style to display string. */
    private formatByStyle(count: number, style: CounterStyle): string {
        switch (style) {
            case 'upper-alpha': return this.toAlpha(count).toUpperCase();
            case 'lower-alpha': return this.toAlpha(count).toLowerCase();
            case 'upper-roman': return this.toRoman(count).toUpperCase();
            case 'lower-roman': return this.toRoman(count).toLowerCase();
            case 'decimal':     return String(count);
            case 'compound-lower-alpha': return String(count); // fallback; handled above
        }
    }

    /** 1→A, 2→B, …, 26→Z, 27→AA, 28→AB … */
    private toAlpha(n: number): string {
        let result = '';
        while (n > 0) {
            const rem = (n - 1) % 26;
            result = String.fromCharCode(65 + rem) + result;
            n = Math.floor((n - 1) / 26);
        }
        return result;
    }

    /** 1→I, 2→II, … */
    private toRoman(n: number): string {
        const vals = [1000,900,500,400,100,90,50,40,10,9,5,4,1];
        const syms = ['M','CM','D','CD','C','XC','L','XL','X','IX','V','IV','I'];
        let result = '';
        for (let i = 0; i < vals.length; i++) {
            while (n >= vals[i]) {
                result += syms[i];
                n -= vals[i];
            }
        }
        return result;
    }
}
